import { useEffect, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { BUSINESS, IMG, NAV } from "@/lib/content";
import { scrollToHash, goToEstimate } from "@/lib/scroll";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToHash(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "bg-[#0B0F17]/85 backdrop-blur-xl border-b border-white/[0.06] py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-5 sm:px-8">
        <a href="#top" onClick={(e) => go(e, "#top")} className="flex items-center gap-3" data-testid="nav-logo">
          <img src={IMG.mark} alt="KS Granite House logo" className="h-10 w-auto mix-blend-lighten sm:h-12" />
          <span className="whitespace-nowrap leading-none">
            <span className="block font-[Cinzel] text-[15px] tracking-[0.18em] text-[#ECE6DA]">GRANITE HOUSE</span>
            <span className="mt-1 block text-[10px] uppercase tracking-[0.42em] text-[#CDA85B]">Kansas City · LLC</span>
          </span>
        </a>

        <nav className="hidden items-center gap-7 xl:flex" aria-label="Primary">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={(e) => go(e, n.href)}
              data-testid={`nav-link-${slug(n.label)}`}
              className="ks-navlink text-[13px] uppercase tracking-[0.16em] text-[#D6D3CC] hover:text-white"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href={BUSINESS.phoneHref} data-testid="nav-call-link" className="hidden items-center gap-2 text-sm text-[#ECE6DA] hover:text-[#CDA85B] lg:flex">
            <Phone className="h-4 w-4 text-[#CDA85B]" /> {BUSINESS.phone}
          </a>
          <span className="hidden md:block">
            <button onClick={() => goToEstimate()} data-testid="nav-quote-btn" className="ks-btn-gold">
              Free Estimate
            </button>
          </span>
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-menu-toggle"
            className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white xl:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            aria-label="Mobile"
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="mx-4 mt-3 rounded-2xl border border-white/10 bg-[#0E131D] p-6 xl:hidden"
          >
            {NAV.map((n, i) => (
              <a
                key={n.href}
                href={n.href}
                onClick={(e) => go(e, n.href)}
                data-testid={`mobile-nav-link-${slug(n.label)}`}
                className="flex items-baseline justify-between border-b border-white/[0.06] py-3 font-display text-2xl text-[#ECE6DA]"
              >
                {n.label}
                <span className="text-xs text-[#CDA85B]">0{i + 1}</span>
              </a>
            ))}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <a href={BUSINESS.phoneHref} className="ks-btn-ghost justify-center" data-testid="mobile-menu-call">Call</a>
              <button onClick={() => { setOpen(false); goToEstimate(); }} className="ks-btn-gold justify-center" data-testid="mobile-menu-quote">Get a Quote</button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
